<script>
import padStart from 'lodash/padStart'
import { set, getHours, getMinutes } from 'date-fns'

export default {
  name: 'BaseTimePicker',
  inheritAttrs: false,
  model: {
    event: 'update',
  },
  props: {
    value: {
      type: Date,
      default: new Date(),
    },
    label: {
      type: String,
      default: null,
    },
    vuelidate: {
      type: Object,
      default: () => {},
    },
    required: Boolean,
  },
  data: () => ({
    hours: '12',
    minutes: '00',
    anteMeridiem: true,
  }),
  computed: {
    formattedHours: {
      get() {
        return padStart(this.hours, 2, '0')
      },
      set(val) {
        this.hours = parseInt(val)
      },
    },
    formattedMinutes: {
      get() {
        return padStart(this.minutes, 2, '0')
      },
      set(val) {
        this.minutes = parseInt(val)
      },
    },
    getAnteMeridiem() {
      return this.anteMeridiem ? 'AM' : 'PM'
    },
    hasErrors() {
      return this.vuelidate && this.vuelidate.$error
    },
  },
  mounted() {
    this.hours = getHours(this.value)
    if (this.hours > 12) {
      this.hours = this.hours - 12
      this.anteMeridiem = false
    }
    this.minutes = getMinutes(this.value)

    this.$watch(
      (vm) => [vm.hours, vm.minutes, vm.anteMeridiem],
      (val) => this.$emit('update', this.formatTime()),
      { immediate: true, deep: true }
    )
  },
  methods: {
    incrementHour() {
      this.hours < 12 ? this.hours++ : (this.hours = 1)
      if (this.hours === 12) this.toggleAnteMeridiem()
    },
    decrementHour() {
      this.hours > 1 ? this.hours-- : (this.hours = 12)
      if (this.hours === 12) this.toggleAnteMeridiem()
    },
    incrementMinute() {
      return this.minutes < 59 ? this.minutes++ : (this.minutes = 0)
    },
    decrementMinute() {
      return this.minutes > 0 ? this.minutes-- : (this.minutes = 59)
    },
    formatTime() {
      return set(new Date(), {
        hours:
          this.hours === 12 && !this.anteMeridiem
            ? 12
            : this.anteMeridiem
            ? this.hours
            : this.hours + 12,
        minutes: this.minutes,
      })
    },
    toggleAnteMeridiem() {
      this.anteMeridiem = !this.anteMeridiem
    },
  },
}
</script>

<template>
  <BaseFormGroup
    :required="required"
    :label="label"
    :vuelidate="vuelidate"
    :has-errors="hasErrors"
  >
    <div class="inline-flex items-center space-x-3">
      <div
        class="inline-flex flex-col items-center w-16 space-y-2 text-blue"
        @keydown.up.prevent="incrementHour"
        @keydown.down.prevent="decrementHour"
      >
        <button @click="incrementHour">
          <BaseIcon name="arrow-up" :size="20" />
        </button>
        <BaseInputText v-model="formattedHours" input-class="text-center" />
        <button @click="decrementHour">
          <BaseIcon name="arrow-down" :size="20" />
        </button>
      </div>

      <div
        class="inline-flex flex-col items-center w-16 space-y-2 text-blue"
        @keydown.up.prevent="incrementMinute"
        @keydown.down.prevent="decrementMinute"
      >
        <button @click="incrementMinute">
          <BaseIcon name="arrow-up" :size="20" />
        </button>
        <BaseInputText v-model="formattedMinutes" input-class="text-center" />
        <button @click="decrementMinute">
          <BaseIcon name="arrow-down" :size="20" />
        </button>
      </div>

      <button
        class="relative inline-flex items-center justify-between w-24 px-3 text-gray-light input border-gray-light"
        @click="toggleAnteMeridiem"
      >
        <span
          :class="anteMeridiem ? 'left-0' : 'right-0'"
          class="absolute inset-y-0 block w-1/2 rounded-full bg-blue"
        />
        <span :class="{ 'text-white': anteMeridiem }" class="relative">
          AM
        </span>
        <span :class="{ 'text-white': !anteMeridiem }" class="relative">
          PM
        </span>
      </button>
    </div>
    <template v-slot:errors>
      <slot name="errors" />
    </template>
  </BaseFormGroup>
</template>
